import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { Mark } from "../components/Mark";
import { Button, Card, ErrorNote, Notice, Spinner } from "../components/primitives";
import { api } from "../lib/api";
import { bytes } from "../lib/format";
import { breakdown, percent, topicOf, uploadSize, untaggedIn } from "../lib/rooms";
import type {
  CallDetails,
  DataRoomSummary,
  RoomKind,
  UploadItem,
  UploadResult,
} from "../lib/types";

/**
 * What a person uploads, and what happens to it.
 *
 * The engagement form used to take a server-side path here, which meant any
 * signed-in user could point a run at any directory the container could read.
 * A room is now a named thing you own; the path is derived server-side and
 * never travels through the browser.
 *
 * **Two kinds, on one page, deliberately.** A client document and an expert
 * transcript are the same object on disk and differ only in what they are
 * evidence *of* — so they are one component with different copy, not two
 * pages. What the separation buys is downstream: the room's kind becomes the
 * source kind in the knowledge graph, so a later run can tell "the company
 * says" from "one person we interviewed says".
 *
 * **Two frames, and they are not symmetric.** A data room's overview is about
 * *that room* — topic, files, size, engagements. The interviews overview is an
 * aggregate over every transcript, because "which industries do our experts
 * cover" is not a question about one call. So the aggregate sits above the
 * list on the right-hand frame and the per-room detail sits under it.
 *
 * The list/detail shape inside each frame is the Deliverables tab's, on
 * purpose: one pattern in this application rather than a second one here.
 */
const KINDS: Record<
  RoomKind,
  {
    title: string;
    blurb: string;
    placeholder: string;
    empty: string;
    unit: string;
  }
> = {
  data_room: {
    title: "Data rooms",
    blurb:
      "Documents an engagement may read. PDFs, decks, Word files and workbooks are converted to text on upload, so the agent can read them and later engagements can search them. Private to you — nobody else can list them or attach them to a run.",
    placeholder: "Project Nimbus — vendor documents",
    empty:
      "No data rooms yet. An engagement without one is an outside-in review, which is a legitimate choice — client documents simply outrank third-party estimates when you have them.",
    unit: "data room",
  },
  interview: {
    title: "Expert interviews",
    blurb:
      "Transcripts of expert calls. Indexed into the knowledge graph as interview evidence, tagged to you, and searchable by your later engagements — an expert who already answered the question is the cheapest research there is.",
    placeholder: "Advanced packaging — M. Josh, 12 Aug",
    empty:
      "No interviews yet. Upload a transcript and its passages become searchable by your later engagements, tagged to the industry the expert covers.",
    unit: "interview",
  },
};

export function DataRooms() {
  return (
    <div className="space-y-6">
      <MemoryBanner />
      <div className="grid gap-6 2xl:grid-cols-2">
        <Frame kind="data_room" />
        <Frame kind="interview" />
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- one frame */

function Frame({ kind }: { kind: RoomKind }) {
  const copy = KINDS[kind];
  const queryClient = useQueryClient();
  const rooms = useQuery({
    queryKey: ["data-rooms", kind],
    queryFn: () => api.dataRooms(kind),
  });
  const [selected, setSelected] = useState<string | null>(null);

  // Both lists and the engagement form's selector, because a create or a
  // delete changes what that selector may offer.
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["data-rooms"] });

  const list = rooms.data ?? [];
  const room = list.find((r) => r.id === selected) ?? list[0] ?? null;

  // A room deleted elsewhere, or one that was never there: the selection has
  // to follow the list rather than point at a gap.
  useEffect(() => {
    if (selected && !list.some((r) => r.id === selected)) setSelected(null);
  }, [list, selected]);

  return (
    <section className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">{copy.title}</h1>
        <p className="mt-1 text-sm text-ink-2">{copy.blurb}</p>
      </div>

      {kind === "interview" && <Aggregate rooms={list} />}

      {rooms.isLoading ? (
        <Card>
          <Spinner />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[14rem_1fr]">
          <RoomList
            kind={kind}
            rooms={list}
            selected={room?.id ?? null}
            onSelect={setSelected}
            onChanged={invalidate}
          />
          {room ? (
            <RoomDetail key={room.id} room={room} onChanged={invalidate} />
          ) : (
            <Card>
              <p className="px-4 py-10 text-center text-sm text-muted">
                {copy.empty}
              </p>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- the list */

function RoomList({
  kind,
  rooms,
  selected,
  onSelect,
  onChanged,
}: {
  kind: RoomKind;
  rooms: DataRoomSummary[];
  selected: string | null;
  onSelect: (id: string) => void;
  onChanged: () => void;
}) {
  const copy = KINDS[kind];
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () => api.createDataRoom(name.trim(), kind),
    onSuccess: (room) => {
      setName("");
      setAdding(false);
      onSelect(room.id);
      onChanged();
    },
  });

  return (
    <Card
      title={`${rooms.length} ${copy.unit}${rooms.length === 1 ? "" : "s"}`}
      action={
        <Button
          size="sm"
          onClick={() => setAdding((open) => !open)}
        >
          {adding ? "Cancel" : "New"}
        </Button>
      }
    >
      {adding && (
        <form
          className="space-y-2 border-b border-line px-3 py-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <label className="field-label" htmlFor={`room-name-${kind}`}>
            Name
          </label>
          <input
            id={`room-name-${kind}`}
            className="field text-sm"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            placeholder={copy.placeholder}
          />
          <Button
            type="submit"
            variant="primary"
            size="sm" className="w-full"
            disabled={!name.trim() || create.isPending}
          >
            Create
          </Button>
          {create.error && <ErrorNote error={create.error} />}
        </form>
      )}

      {/* Scrolls rather than growing the page: the frame beside it is the
          thing being read, and a hundred rooms must not push it off screen. */}
      <ul className="max-h-[28rem] overflow-y-auto py-1">
        {rooms.map((room) => {
          const is = room.id === selected;
          return (
            <li key={room.id}>
              <button
                type="button"
                onClick={() => onSelect(room.id)}
                aria-current={is ? "true" : undefined}
                className={`flex w-full flex-col items-start gap-0.5 border-l-2 px-3 py-2 text-left transition-colors ${
                  is
                    ? "border-accent bg-surface-2"
                    : "border-transparent hover:bg-surface-2"
                }`}
              >
                <span className="line-clamp-2 text-sm">{room.name}</span>
                <span className="font-mono text-xs tabular-nums text-muted">
                  {room.files} file{room.files === 1 ? "" : "s"}
                  {room.locked.length > 0 && (
                    <span className="text-ink-2"> · locked</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/* -------------------------------------------------------------- the detail */

function RoomDetail({
  room,
  onChanged,
}: {
  room: DataRoomSummary;
  onChanged: () => void;
}) {
  /**
   * What the panel below is showing.
   *
   * Held here rather than read off `upload.data`, which is the *upload*
   * mutation's memory of what the server said once. Unlocking a file changes
   * the answer and does not touch that memory, so the "password-protected"
   * warning stayed on screen after the password had worked — the unlock
   * succeeded and the page went on saying it had not (#53).
   */
  const [result, setResult] = useState<UploadResult | null>(null);
  const [confirming, setConfirming] = useState(false);

  const topic = topicOf(room);
  const pending = untaggedIn(room);

  const tag = useMutation({
    mutationFn: () => api.tagDataRoom(room.id),
    onSuccess: onChanged,
  });

  return (
    <Card
      title={room.name}
      action={
        <Button
          variant="danger"
          size="sm"
          onClick={() => setConfirming(true)}
        >
          Delete
        </Button>
      }
    >
      <div className="space-y-4 px-4 py-4">
        <About
          room={room}
          pending={pending}
          onTag={() => tag.mutate()}
          tagging={tag.isPending}
        />
        {tag.error && <ErrorNote error={tag.error} />}
        {topic === null && pending === 0 && room.files === 0 && (
          <p className="text-xs text-muted">
            Nothing in here yet. Add files below and the agent can say what
            they are about.
          </p>
        )}

        <Overview room={room} />

        {room.kind === "interview" && <Call room={room} onChanged={onChanged} />}

        <Upload room={room} onChanged={onChanged} onResult={setResult} />
        {result && (
          <Indexed
            result={result}
            roomId={room.id}
            onChanged={onChanged}
            onResult={setResult}
          />
        )}

        <Files room={room} />
      </div>

      {confirming && (
        <ConfirmDelete
          room={room}
          onCancel={() => setConfirming(false)}
          onDone={() => {
            setConfirming(false);
            onChanged();
          }}
        />
      )}
    </Card>
  );
}

/**
 * What the room is about, and the one button that decides it.
 *
 * The agent's answer is marked as the agent's — provenance, the rule this
 * codebase applies to every number. A label somebody confirmed and one a model
 * proposed are different claims, and the panel says which it is holding.
 */
function About({
  room,
  pending,
  onTag,
  tagging,
}: {
  room: DataRoomSummary;
  pending: number;
  onTag: () => void;
  tagging: boolean;
}) {
  const topic = topicOf(room);
  const nothingToDo = pending === 0 && topic !== null;

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-48 flex-1">
        {topic ? (
          <>
            <div className="text-sm">{topic.topic || "—"}</div>
            <div className="mt-1 flex items-center gap-2">
              {topic.industry && (
                <span className="rounded-sm border border-line px-1.5 py-0.5 font-mono text-xs text-ink-2">
                  {topic.industry}
                </span>
              )}
              <span className="text-xs text-muted">
                {topic.by === "confirmed" ? "confirmed" : "suggested by the agent"}
              </span>
            </div>
          </>
        ) : (
          <div className="text-sm text-muted">Not yet described.</div>
        )}
      </div>
      <Button
        size="sm"
        onClick={onTag}
        disabled={tagging || room.files === 0 || nothingToDo}
        title={
          nothingToDo
            ? "Everything here has been judged already."
            : "One short model call. Only files with no label are read."
        }
      >
        {tagging
          ? "Reading…"
          : pending > 0
            ? `Tag ${pending} file${pending === 1 ? "" : "s"}`
            : "Describe"}
      </Button>
    </div>
  );
}

/** The four numbers a person wants before they open anything. */
function Overview({ room }: { room: DataRoomSummary }) {
  const stats: [string, string, boolean][] = [
    ["Files", String(room.files), false],
    ["Size", bytes(room.size_bytes), false],
    [
      "Engagements",
      String(room.engagements ?? 0),
      false,
    ],
    // Counted in ink. The orange for a locked document is spent once, on the
    // note that asks for its password — the place the reader acts. It was
    // here, on the room list and on that note: three oranges for one fact.
    ["Locked", String(room.locked.length), room.locked.length > 0],
  ];
  return (
    <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-4">
      {stats.map(([label, value, alarming]) => (
        <div key={label} className="bg-surface px-3 py-2">
          <dt className="text-xs text-muted">{label}</dt>
          <dd
            className={`font-mono text-base tabular-nums ${alarming ? "font-medium" : ""}`}
          >
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Every file, with what it was judged to be about. */
function Files({ room }: { room: DataRoomSummary }) {
  const names = Object.keys(room.tags ?? {});
  if (room.files === 0) return null;

  return (
    <details className="rounded-lg border border-line">
      <summary className="cursor-pointer px-3 py-2 text-xs text-ink-2">
        Files ({room.files}
        {names.length > 0 && `, ${names.length} tagged`})
      </summary>
      <ul className="max-h-64 overflow-y-auto border-t border-line px-3 py-2 text-xs">
        {[...names].sort().map((name) => (
          <li key={name} className="flex items-baseline justify-between gap-3 py-0.5">
            <span className="truncate font-mono text-xs">{name}</span>
            <span className="shrink-0 text-muted">{room.tags[name]?.industry ?? "—"}</span>
          </li>
        ))}
        {names.length < room.files && (
          <li className="py-0.5 text-muted">
            {room.files - names.length} file
            {room.files - names.length === 1 ? "" : "s"} with no label yet.
          </li>
        )}
      </ul>
    </details>
  );
}

/**
 * What the call was — the expert, the network, the terms.
 *
 * A **form**, and that is what keeps it out of the rule that ingestion may not
 * create entities: the rule exists to keep an extraction model out of a path
 * that costs no tokens, and a person typing what they know is not extraction.
 *
 * It applies to transcripts uploaded **after** it, because the graph's
 * `:Interview` is written as a file is ingested — so the panel says so rather
 * than letting somebody describe a call and wonder why the transcript they
 * uploaded first is not attached to it.
 *
 * Collapsed by default: most of the time the interesting thing about a room is
 * its transcripts, and nine fields standing open above them is nine fields of
 * chrome.
 */
function Call({
  room,
  onChanged,
}: {
  room: DataRoomSummary;
  onChanged: () => void;
}) {
  const held = (room.call ?? {}) as CallDetails;
  const [draft, setDraft] = useState<CallDetails>(held);
  const [open, setOpen] = useState(false);

  const save = useMutation({
    mutationFn: () => api.describeCall(room.id, draft),
    onSuccess: onChanged,
  });

  const described = Object.keys(held).length > 0;

  return (
    <details
      className="rounded-lg border border-line"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer px-3 py-2 text-xs text-ink-2">
        The call
        {described ? (
          <span className="text-muted">
            {" — "}
            {[held.expert, held.network, held.date].filter(Boolean).join(" · ") ||
              "described"}
          </span>
        ) : (
          <span className="text-muted"> — not described yet</span>
        )}
      </summary>
      <form
        className="space-y-3 border-t border-line px-3 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="text-xs text-muted">
          Applies to transcripts uploaded after it: the graph writes the call as
          a file is ingested, so re-upload a transcript to attach one.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {CALL_FIELDS.map(({ key, label, hint, options }) => (
            <label key={key} className="block">
              <span className="field-label">{label}</span>
              {options ? (
                <select
                  className="field text-sm"
                  value={String(draft[key] ?? "")}
                  onChange={(e) =>
                    setDraft({ ...draft, [key]: e.target.value || undefined })
                  }
                >
                  <option value="">—</option>
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="field text-sm"
                  value={String(draft[key] ?? "")}
                  placeholder={hint}
                  onChange={(e) =>
                    setDraft({ ...draft, [key]: e.target.value || undefined })
                  }
                />
              )}
            </label>
          ))}
        </div>
        {save.error && <ErrorNote error={save.error} />}
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </form>
    </details>
  );
}

/**
 * The fields worth asking for, and no more.
 *
 * The declaration on `:Interview` carries thirteen; these are the ones a
 * person actually knows when they upload a transcript. `interviewer` and
 * `engagement` are derived server-side or not at all, and asking for a field
 * nobody fills is a field that makes the form look longer than it is.
 *
 * `reuse` and `transcript_quality` are the two that change what the system
 * may do: `engagement_only` scopes everything derived from the call, and a
 * notes-only transcript may not carry a verbatim quote.
 */
const CALL_FIELDS: {
  key: keyof CallDetails;
  label: string;
  hint?: string;
  options?: string[];
}[] = [
  { key: "expert", label: "Expert", hint: "Former VP Sales, EU competitor" },
  { key: "network_id", label: "Network id", hint: "glg:1234567" },
  { key: "network", label: "Network", hint: "GLG, AlphaSights, Tegus" },
  { key: "network_ref", label: "Call reference", hint: "the network's call id" },
  { key: "date", label: "Date", hint: "2026-09-14" },
  {
    key: "vantage",
    label: "Vantage",
    options: [
      "current_employee",
      "former_employee",
      "customer",
      "former_customer",
      "lost_prospect",
      "competitor",
      "supplier",
      "channel_partner",
      "industry_observer",
      "regulator",
      "other",
    ],
  },
  {
    key: "channel",
    label: "Channel",
    options: [
      "expert_network",
      "transcript_library",
      "client_introduced",
      "direct",
      "management_access",
    ],
  },
  {
    key: "transcript_quality",
    label: "Transcript",
    options: ["verbatim", "cleaned", "notes", "summary"],
  },
  { key: "reuse", label: "Reuse", options: ["firm_wide", "engagement_only"] },
  { key: "retain_until", label: "Retain until", hint: "2028-09-14" },
];

/* ------------------------------------------------------------- the deleting */

/**
 * Always confirmed, and the dialog names what is destroyed.
 *
 * `window.confirm` was what this was, and it cannot say "24 files, 412 MB,
 * used by 3 engagements" in a way anybody reads — it is one line of text in a
 * browser chrome people dismiss by reflex. Typing the room's name is asked for
 * **only when there is something in it**: a confirmation nobody can fail is a
 * confirmation nobody reads, and one demanded for an empty room is friction
 * with no risk behind it.
 */
function ConfirmDelete({
  room,
  onCancel,
  onDone,
}: {
  room: DataRoomSummary;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [typed, setTyped] = useState("");
  const remove = useMutation({
    mutationFn: () => api.deleteDataRoom(room.id),
    onSuccess: onDone,
  });
  const needsName = room.files > 0;
  const ready = !needsName || typed.trim() === room.name;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Delete ${room.name}`}
      onKeyDown={(e) => e.key === "Escape" && onCancel()}
    >
      <div className="card w-full max-w-md space-y-4 p-5">
        <h2 className="text-sm font-semibold">Delete “{room.name}”?</h2>
        <p className="text-sm text-ink-2">
          This removes{" "}
          <span className="font-mono tabular-nums">{room.files}</span> file
          {room.files === 1 ? "" : "s"} and{" "}
          <span className="font-mono tabular-nums">{bytes(room.size_bytes)}</span>{" "}
          from disk.{" "}
          {room.engagements > 0 && (
            <>
              <span className="font-mono tabular-nums">{room.engagements}</span>{" "}
              engagement{room.engagements === 1 ? "" : "s"} used it — they keep
              their findings and their deliverables, but the documents behind
              them will be gone.
            </>
          )}
        </p>
        {needsName && (
          <div>
            <label className="field-label" htmlFor="confirm-room-name">
              Type the name to confirm
            </label>
            <input
              id="confirm-room-name"
              className="field text-sm"
              value={typed}
              autoFocus
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
              placeholder={room.name}
            />
          </div>
        )}
        {remove.error && <ErrorNote error={remove.error} />}
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!ready || remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- the uploading */

/** How many files go up in one request. A folder drop can be four hundred
 *  documents; one multipart body of those is a request nothing can report
 *  progress on and a proxy may well refuse. */
const CHUNK = 20;

/**
 * One control taking a folder *and* loose files.
 *
 * Two pickers behind one surface rather than two buttons, because "add what I
 * have" is one intention — the browser is what insists a directory picker and
 * a file picker are different inputs. Dropping works for both, which is the
 * only route that needs no choice at all.
 */
function Upload({
  room,
  onChanged,
  onResult,
}: {
  room: DataRoomSummary;
  onChanged: () => void;
  onResult: (result: UploadResult) => void;
}) {
  const files = useRef<HTMLInputElement>(null);
  const folder = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [nothing, setNothing] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [size, setSize] = useState(0);
  const [sent, setSent] = useState(0);

  /**
   * Getting the window back is what says the picker has closed.
   *
   * It matters because the box hides its own buttons while it waits: a dialog
   * somebody thought better of fires no `change`, and without this the box
   * would wait for ever on files that are never coming, with no way back. The
   * `cancel` event would say it precisely and is not an option — React 18 does
   * not carry it for an input, and it is too new to lean on regardless. Focus
   * returning while the dialog is still open only shows the buttons again,
   * which is the harmless direction to be wrong in.
   */
  useEffect(() => {
    if (!waiting) return;
    const settle = () => setWaiting(false);
    window.addEventListener("focus", settle);
    return () => window.removeEventListener("focus", settle);
  }, [waiting]);

  const upload = useMutation({
    // Sequential batches, so the bar is a real fraction rather than an
    // animation: each answer is a whole chunk that actually landed.
    mutationFn: async (items: UploadItem[]) => {
      setTotal(items.length);
      setDone(0);
      setSize(uploadSize(items));
      setSent(0);
      let latest: UploadResult | null = null;
      const added: string[] = [];
      const refused: Record<string, string> = {};
      const ignored: string[] = [];
      // Bytes a finished batch has accounted for. The one in flight adds its
      // own fraction on top of that, so the bar crosses a batch boundary
      // without ever going backwards.
      let acknowledged = 0;
      for (let i = 0; i < items.length; i += CHUNK) {
        const batch = items.slice(i, i + CHUNK);
        const batchSize = uploadSize(batch);
        latest = await api.uploadToDataRoom(room.id, batch, undefined, (fraction) =>
          setSent(acknowledged + batchSize * fraction),
        );
        acknowledged += batchSize;
        setSent(acknowledged);
        added.push(...latest.added);
        Object.assign(refused, latest.refused);
        ignored.push(...latest.ignored);
        setDone(Math.min(i + CHUNK, items.length));
        onChanged();
      }
      // The last answer describes the room as it now stands; the tallies are
      // the whole drop's, or a four-hundred-file upload would report on its
      // last twenty.
      return latest && { ...latest, added, refused, ignored };
    },
    onSuccess: (latest) => {
      if (latest) onResult(latest);
      onChanged();
    },
  });

  /**
   * A pick that hands over no files is a thing to say, not a thing to ignore.
   *
   * Dropping it silently is indistinguishable from a dead button, and the
   * browser produces an empty list for reasons a person cannot see: a folder
   * holding only hidden files, and — on a Mac — a folder Chrome has not been
   * allowed to read, where the picker returns and nothing arrives. That is how
   * "I chose a folder and nothing happened" comes about, with no request made,
   * no progress to watch and nothing on screen to explain it.
   */
  const send = (items: UploadItem[]) => {
    setWaiting(false);
    setNothing(items.length === 0);
    if (items.length) upload.mutate(items);
  };

  // From the press of a button to the last answer, this box is working: the
  // browser reading a folder takes as long as it takes and reports nothing
  // while it does, so the wait starts before there is a byte to measure.
  const busy = waiting || upload.isPending;
  // Sending is what the bar measures. `size` is legitimately zero — a folder
  // of empty files — so the count stands in rather than a division by nothing.
  const share = size > 0 ? sent / size : total > 0 ? done / total : 0;

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void dropped(e.dataTransfer).then(send);
        }}
        className={`rounded-lg border border-dashed px-4 py-5 text-center text-xs transition-colors ${
          over ? "border-accent bg-surface-2" : "border-line-strong"
        }`}
      >
        {busy ? (
          <Sending share={share} sent={sent} size={size} waiting={waiting} />
        ) : (
          <>
            <p className="text-ink-2">
              Drop files or a folder here — subfolders are kept.
            </p>
            <div className="mt-2 flex justify-center gap-2">
              <Button
                size="sm"
                onClick={() => {
                  setWaiting(true);
                  files.current?.click();
                }}
              >
                Choose files
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setWaiting(true);
                  folder.current?.click();
                }}
              >
                Choose a folder
              </Button>
            </div>
          </>
        )}
        {/* Outside the branch above: an input that unmounts mid-pick takes the
            selection with it. */}
        <input
          ref={files}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            send(picked(e.target.files));
            e.target.value = "";
          }}
        />
        <input
          ref={folder}
          type="file"
          multiple
          className="hidden"
          // Not in React's typings, and the two spellings are what the
          // browsers between them actually read.
          {...{ webkitdirectory: "", directory: "" }}
          onChange={(e) => {
            send(picked(e.target.files));
            e.target.value = "";
          }}
        />
      </div>

      {nothing && (
        <p className="mt-2 text-xs text-muted">
          Nothing came through from that pick — not one file, so there was
          nothing to send. An empty folder does that, so does one holding only
          hidden files, and so does a folder this browser has not been given
          access to read.
        </p>
      )}
      {upload.error && <ErrorNote error={upload.error} />}
    </div>
  );
}

/**
 * The box while it is working: the mark drawing itself, over what has gone.
 *
 * The looping mark is this app's one animation for *somebody is waiting*, and
 * an upload qualifies on the rule `Mark` states — the person cannot leave the
 * page and has nothing else to read. Mounted with the wait and unmounted with
 * it, so it never becomes a logo that will not settle.
 *
 * **The bar is bytes, not files.** Twenty documents where the last one is the
 * CIM is a bar that reaches 95% in a second and stays there for the rest of the
 * wait, which is worse than no bar: it says finished when it means started. The
 * size of what was picked is the only estimate available before the server has
 * seen any of it.
 *
 * It reaches 100% when the last byte has gone, which is not when the upload is
 * over — a scanned CIM is real work to convert and index. So 100% says what it
 * actually knows, and the mark keeps moving until the answer lands.
 */
function Sending({
  share,
  sent,
  size,
  waiting,
}: {
  share: number;
  sent: number;
  size: number;
  waiting: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-2.5">
      <Mark size={44} animate loop />
      <div className="w-full max-w-72">
        <div className="h-1 overflow-hidden rounded-sm bg-surface-2">
          <div
            className="h-full bg-accent transition-[width]"
            style={{ width: percent(share) }}
          />
        </div>
        <p className="mt-1.5 font-mono text-xs tabular-nums text-muted">
          {waiting
            ? "Reading your selection…"
            : share >= 1
              ? "100% sent · reading them now"
              : `${percent(share)} · ${bytes(Math.round(sent))} of ${bytes(size)}`}
        </p>
      </div>
    </div>
  );
}

/** Files chosen through a dialog. A folder picked that way carries
 *  `webkitRelativePath`; loose files do not and are their own name. */
function picked(list: FileList | null): UploadItem[] {
  return Array.from(list ?? []).map((file) => ({
    file,
    path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
  }));
}

/**
 * Files dragged in, folders walked.
 *
 * `DataTransfer.files` is flat and drops every directory, so a dropped folder
 * arrives as nothing at all. `webkitGetAsEntry` is the only route to its
 * contents — and **`readEntries` returns at most 100 at a time**, which is the
 * trap: called once, a folder of four hundred documents silently uploads its
 * first hundred. So it is called until it answers empty.
 */
async function dropped(transfer: DataTransfer): Promise<UploadItem[]> {
  type Entry = {
    isFile: boolean;
    isDirectory: boolean;
    name: string;
    file(cb: (f: File) => void, err: (e: unknown) => void): void;
    createReader(): {
      readEntries(cb: (entries: Entry[]) => void, err: (e: unknown) => void): void;
    };
  };

  const roots = Array.from(transfer.items)
    .map((item) => (item.webkitGetAsEntry?.() ?? null) as Entry | null)
    .filter((entry): entry is Entry => entry !== null);

  // No entry API — an older browser, or a drag that carried plain files.
  if (roots.length === 0) return picked(transfer.files);

  const out: UploadItem[] = [];
  const walk = async (entry: Entry, prefix: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        entry.file(resolve, reject),
      );
      out.push({ file, path: prefix + entry.name });
      return;
    }
    const reader = entry.createReader();
    for (;;) {
      const batch = await new Promise<Entry[]>((resolve, reject) =>
        reader.readEntries(resolve, reject),
      );
      if (batch.length === 0) break;
      for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
    }
  };

  for (const root of roots) await walk(root, "");
  return out;
}

/* ------------------------------------------------------------ the aggregate */

/**
 * What our experts cover, over **files**.
 *
 * A room holding six transcripts contributes six: the question is what the
 * set covers, and counting rooms would let one interview filed on its own
 * outweigh six filed together. The untagged count sits beside the total and
 * is never folded into the shares — twelve of fourteen tagged and a bar
 * reading "70%" is a percentage of something the reader believes is fourteen.
 *
 * Five rows of label, bar and a mono percentage. No chart library: this SPA
 * ships none by design, and a horizontal bar is a div with a width.
 */
function Aggregate({ rooms }: { rooms: DataRoomSummary[] }) {
  const shape = breakdown(rooms);
  if (shape.files === 0) return null;

  return (
    <Card title="Where the experts come from">
      <div className="space-y-3 px-4 py-4">
        <p className="font-mono text-xs tabular-nums text-ink-2">
          {shape.files} transcript{shape.files === 1 ? "" : "s"} ·{" "}
          {rooms.length} room{rooms.length === 1 ? "" : "s"}
          {shape.untagged > 0 && (
            <span className="text-muted"> · {shape.untagged} not yet tagged</span>
          )}
        </p>
        {shape.tagged === 0 ? (
          <p className="text-xs text-muted">
            Nothing is tagged yet, so there is no breakdown to draw. Open a room
            and press Tag.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {shape.shares.map((row) => (
              <li key={row.industry} className="flex items-center gap-3 text-xs">
                <span className="w-44 shrink-0 truncate text-ink-2">
                  {row.industry}
                </span>
                <span className="h-2 flex-1 overflow-hidden rounded-sm bg-surface-2">
                  <span
                    className="block h-full rounded-sm bg-accent"
                    style={{ width: `${Math.max(2, row.share * 100)}%` }}
                  />
                </span>
                <span className="w-10 shrink-0 text-right font-mono tabular-nums">
                  {percent(row.share)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted">
          Shares are of the {shape.tagged} tagged transcript
          {shape.tagged === 1 ? "" : "s"}, not of all {shape.files}.
        </p>
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- the graph */

/**
 * Said once, at the top, because it is true of every upload on this page.
 *
 * A graph one schema version behind the code opens read-only — correctly; the
 * alternative is writing new-shaped keys into an old graph. But then every
 * document is stored, converted, reported as uploaded, and **not indexed**,
 * and the only trace was a line in one file's skip note on a panel that had
 * already been replaced. A whole-system condition belongs above the things it
 * affects, not inside one of them.
 */
function MemoryBanner() {
  const status = useQuery({
    queryKey: ["status"],
    queryFn: api.status,
    // Not polled: it changes when an operator migrates or restarts something,
    // and a page that asks every few seconds would open a database connection
    // every few seconds to hear the same answer.
    staleTime: 60_000,
  });
  const memory = status.data?.memory;
  if (!memory || (memory.writable && memory.reachable)) return null;

  return (
    // A state of the system, not something on this page to act on: warning,
    // not the finding's orange.
    <Notice
      tone="warning"
      title={
        memory.migration_required
          ? "The knowledge graph is awaiting a migration."
          : "The knowledge graph is not reachable."
      }
    >
      <p className="text-xs text-ink-2">
        {memory.migration_required ? (
          <>
            It is on schema v{memory.schema} and this version expects v
            {memory.expected_schema}, so it will not accept new documents.
            Uploads are still stored and a run can read them off disk — but
            nothing uploaded now is searchable by later engagements until{" "}
            <code className="font-mono">cdd memory --migrate</code> has run.
          </>
        ) : (
          <>
            Uploads are still stored and a run can read them off disk, but
            nothing is being indexed for later engagements to find.
          </>
        )}
      </p>
    </Notice>
  );
}

/**
 * What the upload put into the graph, and what it did not.
 *
 * Shown rather than swallowed because the gaps are real and none of them is
 * obvious from the outside: a scanned PDF converts to nothing, a slide of
 * pictures holds no text, a spreadsheet is stored but not read. A count with
 * no reasons would leave the person guessing which of those they hit — and
 * a document that is in the room but not in the graph is exactly the thing
 * somebody assumes is there six weeks later.
 *
 * `ignored` is deliberately a bare number: `.DS_Store` and `__MACOSX` are what
 * a folder drop always contains, and naming four of them would make a clean
 * upload read as four problems.
 */
function Indexed({
  result,
  roomId,
  onChanged,
  onResult,
}: {
  result: UploadResult;
  roomId: string;
  onChanged: () => void;
  onResult: (result: UploadResult) => void;
}) {
  const { ingested, skipped, note, locked } = result.indexed;
  const passages = ingested.reduce((n, doc) => n + doc.passages, 0);
  const missed = Object.entries(skipped);
  const refused = Object.entries(result.refused ?? {});
  const ignored = result.ignored?.length ?? 0;

  return (
    <dl className="space-y-1 border-t border-line pt-3 text-xs">
      {ingested.length > 0 && (
        <div className="text-ink-2">
          Indexed {ingested.length} document
          {ingested.length === 1 ? "" : "s"} · {passages} passage
          {passages === 1 ? "" : "s"} searchable
        </div>
      )}
      {note && <div className="text-muted">{note}</div>}
      {refused.map(([file, why]) => (
        <div key={file} className="text-muted">
          <span className="font-mono">{file}</span> — not stored: {why}
        </div>
      ))}
      {ignored > 0 && (
        <div className="text-muted">
          {ignored} system file{ignored === 1 ? "" : "s"} skipped
          (.DS_Store and the like).
        </div>
      )}
      {missed.map(([file, why]) => (
        <div key={file} className="text-muted">
          <span className="font-mono">{file}</span> — {why}
        </div>
      ))}
      {locked?.length > 0 && (
        <Unlock
          files={locked}
          roomId={roomId}
          onChanged={onChanged}
          onResult={onResult}
        />
      )}
    </dl>
  );
}

/**
 * Ask for the password, and only when there is something to unlock.
 *
 * Not a field on the upload form. Almost no upload needs one, and a password
 * box standing open on every screen invites somebody to type one somewhere it
 * was never wanted. The exception asks for itself — the same instinct as the
 * private stamp on a playbook tile: exactly one thing on screen should be
 * asking for attention, and here it has earned it.
 *
 * One field for the set, because a bank sends a set of documents under a
 * single password. It is held in component state, sent, and never persisted:
 * the server converts the document once and stores nothing.
 */
function Unlock({
  files,
  roomId,
  onChanged,
  onResult,
}: {
  files: string[];
  roomId: string;
  onChanged: () => void;
  onResult: (result: UploadResult) => void;
}) {
  const [password, setPassword] = useState("");
  const unlock = useMutation({
    // One password for the set, and the *last* answer describes the room as it
    // now stands — which is what the panel re-renders from.
    mutationFn: async () => {
      let latest: UploadResult | null = null;
      for (const file of files) {
        latest = await api.unlockDataRoomFile(roomId, file, password);
      }
      return latest;
    },
    onSuccess: (latest) => {
      setPassword("");
      if (latest) onResult(latest);
      onChanged();
    },
  });

  return (
    // The one orange on this page: the place a locked document gets unlocked.
    <Notice tone="attention" compact className="mt-2">
      <div>
        {files.length === 1 ? (
          <>
            <span className="font-mono">{files[0]}</span> is password-protected.
          </>
        ) : (
          <>{files.length} files are password-protected.</>
        )}{" "}
        They are stored, but nothing can read them until you supply it.
      </div>
      <form
        className="mt-2 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (password) unlock.mutate();
        }}
      >
        <input
          type="password"
          value={password}
          autoComplete="off"
          placeholder="Document password"
          onChange={(e) => setPassword(e.target.value)}
          className="field field-sm w-48"
        />
        <Button
          size="sm"
          disabled={!password || unlock.isPending}
        >
          {unlock.isPending ? "Unlocking…" : "Unlock"}
        </Button>
      </form>
      {unlock.error && <ErrorNote error={unlock.error} />}
    </Notice>
  );
}
